import { useEffect, useState, ChangeEvent } from 'react';
import Cookies from 'universal-cookie';
import InputField from './InputField';
import BeerCarbonation from './BeerCarbonation';
import Card, { CollasableCard } from './Cards';
import { useTranslation } from 'react-i18next';

const cookies = new Cookies();

type SugarType = 'TABLE' | 'CORN' | 'DME' | 'HONEY' | 'GOZDAWA';

const carbonationFactor: Record<SugarType, number> = { TABLE: 3.82, CORN: 4.02, DME: 6.8, HONEY: 4.55, GOZDAWA: 5.05287 };

interface CarbonationState {
  beerVolume: string | number;
  CO2Level: string | number;
  beerTemp: string | number;
  sugarType: SugarType;
  orginalGravity: string | number;
  sugarAmount?: string;
  waterAmount?: string;
}

function calcCarbonation(
  beerVolume: string | number,
  CO2Level: string | number,
  beerTemp: string | number,
  sugarType: SugarType,
  orginalGravity: string | number
): CarbonationState {
  const p = Number(beerTemp) * (9 / 5) + 32;
  const greenBeerCo2 = 3.0378 - (0.050062 * p) + (0.00026555 * Math.pow(p, 2));
  const n = (Number(CO2Level) - greenBeerCo2) * Number(beerVolume) * carbonationFactor[sugarType];
  let sugarAmount: string | undefined;
  let waterAmount: string | undefined;
  if (n > 0) {
    sugarAmount = n.toFixed(0);
    waterAmount = (n * 100 / Number(orginalGravity) - n).toFixed(0);
  }
  return { beerVolume, CO2Level, beerTemp, sugarType, orginalGravity, sugarAmount, waterAmount };
}

export default function Carbonation() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('carbonation.pageTitle');
  }, [t]);

  const [state, setState] = useState<CarbonationState>(() =>
    calcCarbonation(
      cookies.get('carbonation.beerVolume') || 20,
      cookies.get('carbonation.CO2Level') || 2,
      cookies.get('carbonation.beerTemp') || 18,
      (cookies.get('carbonation.sugarType') as SugarType) || 'CORN',
      cookies.get('carbonation.orginalGravity') || 12,
    )
  );

  function handleOnChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = event.target;
    cookies.set(`carbonation.${name}`, value);
    const s = { ...state, [name]: value };
    setState(calcCarbonation(s.beerVolume, s.CO2Level, s.beerTemp, s.sugarType, s.orginalGravity));
  }

  return (
    <div>
      <CollasableCard title={t('carbonation.infoTitle')}>
        <p className="text-muted-foreground text-sm mb-2">
          {t('carbonation.infoLine1')}
        </p>
        <ul className="text-muted-foreground text-sm list-disc list-inside space-y-1">
          <li>{t('carbonation.infoBullet1')}</li>
          <li>{t('carbonation.infoBullet2')}</li>
          <li>{t('carbonation.infoBullet3')}</li>
        </ul>
      </CollasableCard>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card title={t('carbonation.cardTitle')}>
          <InputField name="beerVolume" label={t('carbonation.beerVolume')} step="0.1" value={state.beerVolume} unit="L" onChange={handleOnChange} />
          <InputField name="CO2Level" label={t('carbonation.co2Level')} step="0.1" value={state.CO2Level} onChange={handleOnChange} />

          <div className="mb-3">
            <label className="mb-1 font-medium text-foreground text-xs block">{t('carbonation.sugarType')}</label>
            <select
              className="w-full border border-input bg-background text-foreground text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring"
              name="sugarType"
              value={state.sugarType}
              onChange={handleOnChange}
            >
              <option value="CORN">{t('carbonation.sugarGlucose')}</option>
              <option value="TABLE">{t('carbonation.sugarTable')}</option>
              <option value="DME">{t('carbonation.sugarDME')}</option>
              <option value="HONEY">{t('carbonation.sugarHoney')}</option>
              <option value="GOZDAWA">{t('carbonation.sugarGozdawa')}</option>
            </select>
          </div>

          <InputField name="beerTemp" label={t('carbonation.beerTemp')} step="0.1" value={state.beerTemp} unit="°C" onChange={handleOnChange} />
          <InputField label={t('carbonation.sugarAmount')} value={state.sugarAmount} unit="g" readOnly />
          <InputField name="orginalGravity" label={t('carbonation.originalGravity')} step="0.1" value={state.orginalGravity} unit="BLG" onChange={handleOnChange} />
          <InputField label={t('carbonation.waterAmount')} value={state.waterAmount} unit="ml" readOnly />
        </Card>

        <Card title={t('carbonation.co2LevelsTitle')}>
          <BeerCarbonation />
        </Card>
      </div>
    </div>
  );
}
